import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getLeoTimestamp,
  LeoTimestamp,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { APIKey, APIKeyDTO } from "./apiKey";
/* eslint-disable import/export */
export abstract class InvalidateAndRotateAPIKeyRPC implements
  LeoRPC<InvalidateAndRotateAPIKeyRPC.Request, InvalidateAndRotateAPIKeyRPC.Response, InvalidateAndRotateAPIKeyRPC.Errors.Errors>
{
  abstract execute(
    request: InvalidateAndRotateAPIKeyRPC.Request
  ): Promise<LeoRPCResult<InvalidateAndRotateAPIKeyRPC.Response, InvalidateAndRotateAPIKeyRPC.Errors.Errors>>;
}

export namespace InvalidateAndRotateAPIKeyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    apiKeyId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isInvalidateAndRotateAPIKeyRPCRequest: boolean = true;
  
    readonly apiKeyId: LeoUUID;
  
  
    constructor(
    apiKeyId: LeoUUID
    ) {
      this.apiKeyId = apiKeyId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const apiKeyId = getLeoUuid(dto, "apiKeyId", InvalidRequestApiKeyIdError);
      return new Request(
        apiKeyId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          apiKeyId: this.apiKeyId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `InvalidateAndRotateAPIKeyRPC(apiKeyId='${this.apiKeyId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    newAPIKey: APIKeyDTO;
    expiresAt: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isInvalidateAndRotateAPIKeyResponse: boolean = true;
  
    readonly newAPIKey: APIKey;
    readonly expiresAt: LeoTimestamp;
  
  
    constructor(
    newAPIKey: APIKey, 
    expiresAt: LeoTimestamp
    ) {
      this.newAPIKey = newAPIKey;
      this.expiresAt = expiresAt;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const newAPIKey = APIKey.fromDTO(getObject(dto, "newAPIKey", InvalidResponseNewApiKeyError));
      const expiresAt = getLeoTimestamp(dto, "expiresAt", InvalidResponseExpiresAtError);
      return new Response(
        newAPIKey,
        expiresAt
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          newAPIKey: this.newAPIKey.toDTO(),
          expiresAt: this.expiresAt.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `InvalidateAndRotateAPIKey(newAPIKey='${this.newAPIKey}',expiresAt='${this.expiresAt}')`;
  };
  

  export enum RPCError {
    InvalidApiKey = "INVALID_API_KEY"
  }

  export namespace Errors {
    
    export interface InvalidApiKeyDTO {
      code: string;
    }
    
    export class InvalidApiKey extends LeoRPCError<RPCError> {
      readonly isInvalidApiKey: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidApiKey;
        super(code);
      }
    
    
      toDTO(): InvalidApiKeyDTO {
        const dto: InvalidApiKeyDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidApiKey.prototype.toString = function toString(): string {
      return `InvalidateAndRotateAPIKeyRPC.INVALID_API_KEY()`;
    };
    

    export type Errors = InvalidApiKey;
    export type ErrorsDTO = InvalidApiKeyDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidApiKey:
          return new InvalidApiKey();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestApiKeyIdError extends InvalidateAndRotateAPIKeyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestApiKeyIdError";
  }
}

export class InvalidResponseNewApiKeyError extends InvalidateAndRotateAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNewApiKeyError";
  }
}

export class InvalidResponseExpiresAtError extends InvalidateAndRotateAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseExpiresAtError";
  }
}

export class InvalidResponseInvalidApiKeyError extends InvalidateAndRotateAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidApiKeyError";
  }
}

